import { useState } from 'react';
import type { Template, TemplateItem } from '../api';

/**
 * A composite template's steps.
 *
 * The owner asked for "composite templates combining multiple templates into one, creating
 * multiple tasks on instantiation". The database has spawned several tasks from `kind='bundle'`
 * since Phase 2 — but a member was a bare string, and there was no way to reach any of it from
 * the app: the forms could not set `kind`, could not add a `task` item, and `EditTemplateForm`
 * blind-carried the items it could not display. The whole feature was curl-only.
 *
 * A member here is a real template. That is what makes "Morning routine" worth having: each step
 * arrives with its own checklist, its own goal, its own estimate and its own habit link, rather
 * than as three empty tasks with the right names.
 */

export interface MemberDraft {
  templateId: string | null;
  label: string;
}

/** What the edit form starts from: the `task` items already on the template. */
export const membersFromItems = (items: readonly TemplateItem[]): MemberDraft[] =>
  items
    .filter((i) => i.kind === 'task')
    .map((i) => ({ templateId: i.memberTemplateId, label: i.label }));

/** What goes back to the API. Order is the array's order — there is no sort field to fight. */
export const membersToItems = (members: readonly MemberDraft[]) =>
  members.map((m) => ({
    kind: 'task' as const,
    label: m.label,
    memberTemplateId: m.templateId,
  }));

export function TemplateMembers({
  members,
  onChange,
  chain,
  onChangeChain,
  candidates,
  idPrefix,
}: {
  members: MemberDraft[];
  onChange: (next: MemberDraft[]) => void;
  chain: boolean;
  onChangeChain: (next: boolean) => void;
  /** Every template that may be a member — bundles excluded, since nesting is forbidden. */
  candidates: Template[];
  idPrefix: string;
}) {
  const [picked, setPicked] = useState('');

  const add = () => {
    const template = candidates.find((t) => t.id === picked);
    if (!template) return;
    onChange([...members, { templateId: template.id, label: template.name }]);
    setPicked('');
  };

  return (
    <fieldset className="template-members">
      <legend>Steps</legend>
      <p className="subtext">
        Each step is another template. Instantiating this creates one task per step, each with that
        template’s own checklist, goal and estimate.
      </p>

      <ol className="plain member-list">
        {members.map((member, index) => (
          <li key={`${member.templateId ?? 'label'}-${index}`} data-testid="member-row">
            <span>{member.label}</span>
            {/* A member whose template was deleted keeps its label as a frozen snapshot — the
                `ON DELETE SET NULL` case, said out loud rather than silently degraded. */}
            {member.templateId === null && <span className="pill warn">template gone</span>}
            <button
              type="button"
              className="link"
              aria-label={`Remove ${member.label}`}
              onClick={() => onChange(members.filter((_, i) => i !== index))}
            >
              remove
            </button>
            <button
              type="button"
              className="link"
              aria-label={`Move ${member.label} up`}
              disabled={index === 0}
              onClick={() => {
                const next = [...members];
                const [moved] = next.splice(index, 1);
                next.splice(index - 1, 0, moved!);
                onChange(next);
              }}
            >
              ↑
            </button>
          </li>
        ))}
      </ol>
      {members.length === 0 && <p className="muted">No steps yet.</p>}

      <div className="row gap">
        <select
          id={`${idPrefix}-member`}
          value={picked}
          aria-label="Add a step"
          onChange={(e) => setPicked(e.target.value)}
        >
          <option value="">Add a step…</option>
          {candidates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.icon ? `${t.icon} ` : ''}
              {t.name}
            </option>
          ))}
        </select>
        <button type="button" disabled={picked === ''} onClick={add}>
          Add
        </button>
      </div>

      {/*
        The flag that decides whether this is a queue or a routine.

        Every bundle chained its members, which is right for trip prep and wrong for a morning
        routine: it hid two of three steps from the planner behind "Blocked by another task".
      */}
      <label className="check">
        <input type="checkbox" checked={chain} onChange={(e) => onChangeChain(e.target.checked)} />
        Steps must be done in order
      </label>
      <p className="subtext">
        {chain
          ? 'Each step waits for the one before it. Suits prep queues — book flights, then book the hotel.'
          : 'Every step is available at once. Suits a routine whose steps do not depend on each other.'}
      </p>
    </fieldset>
  );
}
